import React, { useEffect, useState, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import {
  fetchOrganizations,
  fetchQueueDetails,
  clearPrintQueue,
  setSelectedOrganization,
  clearError,
  checkConfiguration,
  resetClearingProgress,
} from '../redux/reducers/printServerSlice'
import {
  AlertTriangle,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  Info,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Package,
  Printer,
  Clock,
  Hash,
  User,
  Warehouse,
} from 'lucide-react'

const ClearPrintingQueue: React.FC = () => {
  const dispatch = useAppDispatch()
  const {
    organizations,
    selectedOrganization,
    queueDetails,
    isLoading,
    isLoadingDetails,
    isClearing,
    clearingProgress,
    error,
    lastClearResult,
    activityLogs,
    isConfigured,
  } = useAppSelector((state) => state.printServer)

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [expandedLists, setExpandedLists] = useState<Set<number>>(new Set())
  const [showClearingWarning, setShowClearingWarning] = useState(false)

  useEffect(() => {
    // Load organizations and check configuration on mount
    dispatch(checkConfiguration())
    dispatch(fetchOrganizations())
  }, [dispatch])

  // Auto-load queue details when organization is selected
  useEffect(() => {
    if (selectedOrganization && isConfigured) {
      dispatch(fetchQueueDetails(selectedOrganization))
    }
  }, [selectedOrganization, isConfigured, dispatch])

  // Prevent page unload during clearing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isClearing) {
        e.preventDefault()
        e.returnValue = 'Clearing is in progress. Are you sure you want to leave?'
        return 'Clearing is in progress. Are you sure you want to leave?'
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isClearing])

  // Show warning modal when clearing starts
  useEffect(() => {
    setShowClearingWarning(isClearing)
  }, [isClearing])

  const handleOrganizationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setSelectedOrganization(e.target.value))
  }

  const handleRefreshDetails = () => {
    if (selectedOrganization) {
      dispatch(fetchQueueDetails(selectedOrganization))
    }
  }

  const handleClearQueue = () => {
    setShowConfirmDialog(true)
  }

  const confirmClearQueue = () => {
    if (selectedOrganization) {
      dispatch(clearPrintQueue({ 
        organizationCode: selectedOrganization, 
        dryRun: false 
      }))
    }
    setShowConfirmDialog(false)
  }

  const toggleListExpansion = (listId: number) => {
    setExpandedLists(prev => {
      const newSet = new Set(prev)
      if (newSet.has(listId)) {
        newSet.delete(listId)
      } else {
        newSet.add(listId)
      }
      return newSet
    })
  }

  // Calculate totals from queue details
  const calculateQueueTotals = useCallback(() => {
    if (!queueDetails?.data) {
      return { totalLists: 0, totalItems: 0, unprintedItems: 0 }
    }
    
    const totalLists = queueDetails.data.length
    const totalItems = queueDetails.data.reduce((sum, list) => sum + list.items.length, 0)
    const unprintedItems = queueDetails.data.reduce((sum, list) => 
      sum + list.items.filter(item => item.is_printed === 0).length, 0
    )
    
    return { totalLists, totalItems, unprintedItems }
  }, [queueDetails])

  const getActivityIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'info':
        return <Info className="w-4 h-4 text-blue-500" />
      default:
        return <Activity className="w-4 h-4 text-gray-500" />
    }
  }

  const totals = calculateQueueTotals()

  return (
    <div className="space-y-4">
      {/* Header with Queue Summary */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Clear Printing Queue</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage and clear stuck print jobs in the legacy print server system
            </p>
          </div>
          {selectedOrganization && (
            <div className="flex items-center space-x-4">
              <div className="bg-card border border-border rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <Printer className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Pending Items:</span>
                  <span className={`text-lg font-bold ${
                    totals.unprintedItems === 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {totals.unprintedItems}
                  </span>
                </div>
              </div>
              <button
                onClick={handleRefreshDetails}
                disabled={isLoadingDetails || !isConfigured}
                className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isLoadingDetails ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span className="text-sm">Refresh</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Warning */}
      {!isConfigured && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Print Server Not Configured
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                The legacy print server API credentials are not configured. Please contact your system administrator.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Warning Alert */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Important Notice
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              This will mark ALL pending print jobs as completed without actually printing them. 
              Use this only when the print queue is stuck and needs to be reset.
            </p>
          </div>
        </div>
      </div>

      {/* Organization Selector */}
      <div className="bg-card border border-border rounded-lg p-4">
        <label className="block text-sm font-medium text-foreground mb-2">
          Select Organization
        </label>
        <select
          value={selectedOrganization || ''}
          onChange={handleOrganizationChange}
          className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={!isConfigured || organizations.length === 0}
        >
          <option value="">-- Select Organization --</option>
          {organizations.map((org) => (
            <option key={org.code} value={org.code}>
              {org.name} ({org.code})
            </option>
          ))}
        </select>
      </div>

      {/* Queue Details */}
      {queueDetails && selectedOrganization && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Queue Details</h3>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span>{totals.totalLists} lists</span>
              <span>•</span>
              <span>{totals.totalItems} total items</span>
              <span>•</span>
              <span className={`font-medium ${
                totals.unprintedItems === 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {totals.unprintedItems} unprinted
              </span>
            </div>
          </div>

          {queueDetails.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">No items in the print queue</p>
          ) : (
            <>
              <div className="space-y-2 max-h-96 overflow-y-auto">
              {queueDetails.data.map((list) => {
                const isExpanded = expandedLists.has(list.id)
                const unprintedCount = list.items.filter(item => item.is_printed === 0).length
                
                return (
                  <div key={list.id} className="border border-border rounded-md">
                    <button
                      onClick={() => toggleListExpansion(list.id)}
                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                        <Package className="w-4 h-4 text-muted-foreground" />
                        <div className="text-left">
                          <p className="text-sm font-medium">List {list.id} - {list.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Warehouse: {list.warehouse_id} | Items: {list.items.length} 
                            {unprintedCount > 0 && (
                              <span className="text-red-600 ml-1">({unprintedCount} unprinted)</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(list.created_at).toLocaleString()}</span>
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="border-t border-border px-3 py-2 bg-muted/20">
                        <div className="space-y-1">
                          {list.items.map((item) => (
                            <div key={item.id} className="flex items-center justify-between py-1 px-2 hover:bg-muted/50 rounded">
                              <div className="flex items-center space-x-2">
                                <Hash className="w-3 h-3 text-muted-foreground" />
                                <span className="text-xs font-mono">{item.articleNo}</span>
                                <span className="text-xs text-muted-foreground">-</span>
                                <span className="text-xs">{item.name}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                {item.is_printed === 0 ? (
                                  <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Unprinted</span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Printed</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
              </div>
              
              {/* Clear Queue Button */}
              {totals.unprintedItems > 0 && (
                <div className="mt-4">
                  <button
                    onClick={handleClearQueue}
                    disabled={isClearing || !isConfigured}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isClearing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Clearing...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        <span>Clear Print Queue</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Clearing Progress */}
      {clearingProgress && isClearing && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-amber-800 dark:text-amber-200">
              Clearing Queue in Progress
            </h3>
            <AlertTriangle className="w-5 h-5 text-amber-600 animate-pulse" />
          </div>
          
          <div className="space-y-3">
            <div className="w-full bg-amber-200 dark:bg-amber-800 rounded-full h-3">
              <div 
                className="bg-amber-600 dark:bg-amber-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${clearingProgress.percentage}%` }}
              />
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-amber-700 dark:text-amber-300">
                Processing item {clearingProgress.currentItem} of {clearingProgress.totalItems}
              </span>
              <span className="font-medium text-amber-800 dark:text-amber-200">
                {clearingProgress.percentage}%
              </span>
            </div>
            
            {clearingProgress.currentItemName && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Current: {clearingProgress.currentItemName}
              </p>
            )}
            
            <div className="flex items-start space-x-2 mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-500 mt-0.5" />
              <p className="text-xs text-red-700 dark:text-red-300">
                <strong>Do not leave this page!</strong> Closing or refreshing will interrupt the clearing process.
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Last Clear Result */}
      {lastClearResult && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-base font-semibold text-foreground mb-3">
            Last Operation Result
          </h3>
          <div className="space-y-2">
            <p className="text-sm">
              <span className="text-muted-foreground">Status:</span>{' '}
              <span className={`font-medium ${
                lastClearResult.itemsFailed > 0 ? 'text-amber-600' : 'text-green-600'
              }`}>
                {lastClearResult.message}
              </span>
            </p>
            <p className="text-sm">
              <span className="text-muted-foreground">Items Processed:</span>{' '}
              <span className="font-medium text-foreground">{lastClearResult.itemsProcessed}</span>
            </p>
            {lastClearResult.itemsFailed > 0 && (
              <p className="text-sm">
                <span className="text-muted-foreground">Items Failed:</span>{' '}
                <span className="font-medium text-red-600">{lastClearResult.itemsFailed}</span>
              </p>
            )}
            <p className="text-sm">
              <span className="text-muted-foreground">Completed At:</span>{' '}
              <span className="font-medium text-foreground">
                {new Date(lastClearResult.completedAt).toLocaleString()}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button
              onClick={() => dispatch(clearError())}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-base font-semibold text-foreground mb-3">Activity Log</h3>
        {activityLogs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No activity recorded yet</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {activityLogs.map((log, index) => (
              <div key={index} className="flex items-start space-x-3 py-2 border-b border-border last:border-0">
                {getActivityIcon(log.status)}
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{log.action}</p>
                  <p className="text-xs text-muted-foreground">{log.details}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(log.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Confirm Clear Queue
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to clear {totals.unprintedItems} unprinted items from the print queue? This action cannot be undone.
            </p>
            <div className="flex items-start space-x-2 mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                The clearing process may take several minutes. Please keep this page open until it completes.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false)
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmClearQueue}
                className="px-4 py-2 text-white rounded-md bg-red-600 hover:bg-red-700"
              >
                Clear Queue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClearPrintingQueue